import ErrorToast from "../../components/common/toasts/ErrorToast";
import React from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import AccessDenied from "../../components/common/AccessDenied";
import AddCompanyPage from "./pages/company/AddCompanyPage";
import EditCompanyPage from "./pages/company/EditCompanyPage";
import PremiumLayout from "./components/PremiumLayout";
import PremiumBackLink from "./components/PremiumBackLink";
import PremiumTabs from "./components/PremiumTabs";
import CompanyList from "./components/CompanyList";
import CompanyMembers from "./CompanyMembers";
import AddCompanyMemberPage from "./pages/company/AddCompanyMemberPage";
import EditCompanyMemberPage from "./pages/company/EditCompanyMemberPage";
import Dictionaries from "./Dictionaries";

import usePremiumResource from "./hooks/usePremiumResource";
import { premiumPaths } from "./navigation";

export default function PremiumAdmin({ section = "list", action = "list" }) {
  const { accessToken, checkPermission } = useAuth();
  const allowed = checkPermission("Zarządzanie użytkownikami");
  const { companyId } = useParams();
  const companyResource = usePremiumResource(
    accessToken,
    allowed && companyId ? `/organizations/${companyId}` : null,
  );
  const optionsResource = usePremiumResource(
    accessToken,
    allowed && ["settings", "new"].includes(section) ? "/admin/options" : null,
  );
  const company = companyResource.data?.organization;
  const options = optionsResource.data;
  if (!allowed) return <AccessDenied />;
  const label = {
    new: "Dodaj firmę",
    settings: "Ustawienia",
    members: "Użytkownicy",
    dictionaries: "Słowniki",
  }[section];
  return (
    <PremiumLayout
      title={
        section === "list"
          ? "Firmy Premium"
          : section === "new"
            ? "Dodaj firmę Premium"
            : company?.name || "Firma Premium"
      }
      subtitle={company ? label : "Zarządzanie dostępem i konfiguracją firm"}
      actions={
        section === "list" ? (
          <Link className="btn btn-primary" to={premiumPaths.newCompany}>
            Dodaj firmę
          </Link>
        ) : (
          ((companyId && action === "list") ||
            companyResource.error ||
            optionsResource.error) && (
            <PremiumBackLink to={premiumPaths.admin} destination="Lista firm" />
          )
        )
      }
    >
      <ErrorToast message={companyResource.error || optionsResource.error} />
      {(companyResource.error || optionsResource.error) && (
        <button
          className="btn btn-outline-primary"
          onClick={() => {
            companyResource.reload();
            optionsResource.reload();
          }}
        >
          Ponów odczyt
        </button>
      )}
      {(companyResource.loading || optionsResource.loading) && (
        <p role="status">Ładowanie ustawień…</p>
      )}
      {section === "list" && <CompanyList token={accessToken} admin />}
      {company && action === "list" && <PremiumTabs company={company} admin />}
      {options && section === "new" && (
        <AddCompanyPage options={options} token={accessToken} />
      )}
      {options && company && section === "settings" && (
        <EditCompanyPage
          key={companyId}
          company={company}
          options={options}
          token={accessToken}
          onSaved={(updated) =>
            companyResource.replace({ organization: updated })
          }
        />
      )}
      {company && section === "members" && action === "list" && (
        <CompanyMembers key={companyId} company={company} token={accessToken} />
      )}
      {company && section === "members" && action === "new" && (
        <AddCompanyMemberPage
          key={companyId}
          company={company}
          token={accessToken}
        />
      )}
      {company && section === "members" && action === "edit" && (
        <EditCompanyMemberPage
          key={companyId}
          company={company}
          token={accessToken}
        />
      )}
      {company && section === "dictionaries" && (
        <Dictionaries
          key={companyId}
          organization={company}
          token={accessToken}
          admin
          action={action}
        />
      )}
    </PremiumLayout>
  );
}
